import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { QrCode, ArrowLeft, History, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
// import QRScanner from '../../components/QRScanner/QRScanner';
import AssetDetails from "../../components/AssetInspection/AssetDetails";
import InspectionForm, {
  InspectionSubmissionData,
} from "../../components/AssetInspection/InspectionForm";
import InspectionHistory from "../../components/AssetInspection/InspectionHistory";
import InspectionViewer from "../../components/AssetInspection/InspectionViewer";
import ChecklistManager from "../../components/ChecklistManagement/ChecklistManager";
import { useAsset } from "../../store/store";
import { DotLoader } from "../../components/loader/Loader";
import axiosInstance from "../../common/axiosInstance";

type ViewMode =
  | "scanner"
  | "asset-details"
  | "inspection-form"
  | "history"
  | "view-inspection"
  | "checklist-management";

export interface AssetData {
  id?: string;
  _id?: string;
  equipmentInventory: string; // Equipment category ID
  equipmentInventoryName?: string; // Equipment category name (for display)
  equipmentSubcategory?: string; // Subcategory ID
  equipmentSubcategoryName?: string; // Subcategory name (for display)
  location?: string;
  floor: string;
  capacity?: string;
  type?: string;
  cylinderNumber?: string;
  panelNo?: string;
  loopNo?: string;
  deviceNo?: string;
  loopType?: string;
  equipmentManufactureDate?: string;
  hptTestDate?: string;
  lastRefillDate?: string;
  description?: string;
  qty?: string | number;
  premisesName?: string;
  vendorName?: string;
  mobileNumber?: string;
  vendorEmail?: string;
  lastInspectionDate?: string;
  nextInspectionDate?: string;
  inspectionStatus?: 'due' | 'overdue' | 'completed' | 'upcoming';
  HB_No?: string;
}

const AssetInspection: React.FC = () => {
  const { assetId } = useParams<{ assetId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentAssets, getAssetsByIdHandler } = useAsset();

  const [currentView, setCurrentView] = useState<ViewMode>("scanner");
  const [selectedAsset, setSelectedAsset] = useState<AssetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [showQRScanner, setShowQRScanner] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<any>(null);

  useEffect(() => {
    // Check if asset data was passed via navigation state
    const passedAssetData = location.state?.assetData;
    const passedDisplayData = location.state?.displayData;

    if (passedAssetData && assetId) {
      // Use the passed asset data directly
      const transformedAsset: AssetData = {
        id: passedAssetData._id || assetId || "",
        equipmentInventory: passedAssetData.equipmentInventory || "", // Keep ID
        equipmentInventoryName:
          passedDisplayData?.equipmentInventory ||
          passedAssetData.equipmentInventoryName ||
          "Unknown Equipment",
        equipmentSubcategory: passedAssetData.equipmentSubCategory || "", // Keep ID
        equipmentSubcategoryName:
          passedDisplayData?.equipmentSubcategory ||
          passedAssetData.equipmentSubcategoryName ||
          undefined,
        location: passedAssetData.location || "Unknown Location",
        floor: passedAssetData.floor || "Unknown Floor",
        capacity: passedAssetData.capacity,
        type: passedAssetData.type,
        cylinderNumber: passedAssetData.cylinderNumber,
        panelNo: passedAssetData.panelNo,
        loopNo: passedAssetData.loopNo,
        deviceNo: passedAssetData.deviceNo,
        loopType: passedAssetData.loopType,
        equipmentManufactureDate: passedAssetData.equipmentManufactureDate,
        hptTestDate: passedAssetData.hptTestDate,
        lastRefillDate: passedAssetData.lastRefillDate,
        description: passedAssetData.Description || passedAssetData.description,
        qty: passedAssetData.Qty || passedAssetData.qty,
        premisesName: passedAssetData.premisesName,
        vendorName: passedAssetData.vendorName,
        mobileNumber: passedAssetData.mobileNumber,
        vendorEmail: passedAssetData.vendorEmail,
        lastInspectionDate: passedAssetData.checksAssets?.[0]?.createdAt,
        inspectionStatus: calculateInspectionStatus(
          passedAssetData.checksAssets
        ),
        nextInspectionDate: passedAssetData?.nextInspectionDate,
        HB_No: passedAssetData?.HB_No || '',
      };
      setSelectedAsset(transformedAsset);
      setCurrentView("asset-details");
    } else if (assetId) {
      // Fallback to fetching asset data
      fetchAssetData(assetId);
    } else {
      // For web, show scanner option but also allow direct access
      setCurrentView("scanner");
    }
  }, [assetId, location.state]);

  const fetchAssetData = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      // Use existing store method to get asset data
      const assetData = await getAssetsByIdHandler(id);
      if (assetData) {
        // Transform the data to match our interface
        const transformedAsset: AssetData = {
          id: assetData._id || id || "",
          equipmentInventory: assetData.equipmentInventory || "", // Keep ID
          equipmentInventoryName:
            assetData.equipmentInventoryName || "Unknown Equipment",
          equipmentSubcategory: assetData.equipmentSubCategory || "", // Keep ID
          equipmentSubcategoryName:
            assetData.equipmentSubcategoryName || undefined,
          location: assetData.location || "Unknown Location",
          floor: assetData.floor || "Unknown Floor",
          capacity: assetData.capacity,
          type: assetData.type,
          cylinderNumber: assetData.cylinderNumber,
          panelNo: assetData.panelNo,
          loopNo: assetData.loopNo,
          deviceNo: assetData.deviceNo,
          loopType: assetData.loopType,
          equipmentManufactureDate: assetData.equipmentManufactureDate,
          hptTestDate: assetData.hptTestDate,
          lastRefillDate: assetData.lastRefillDate,
          description: assetData.description || assetData.Description,
          qty: assetData.Qty || assetData.qty,
          premisesName: assetData.premisesName,
          vendorName: assetData.vendorName,
          mobileNumber: assetData.mobileNumber,
          vendorEmail: assetData.vendorEmail,
          // Calculate inspection status based on last inspection
          lastInspectionDate: assetData.checksAssets?.[0]?.createdAt,
          inspectionStatus: calculateInspectionStatus(assetData.checksAssets),
          nextInspectionDate: assetData?.nextInspectionDate,
          HB_No: assetData?.HB_No || '',
        };
        setSelectedAsset(transformedAsset);
        setCurrentView("asset-details");
      } else if (currentAssets && currentAssets.assets) {
        // Fallback to currentAssets from store
        const storeAssetData = currentAssets.assets;
        const transformedAsset: AssetData = {
          id: storeAssetData._id || id || "",
          equipmentInventory: storeAssetData.equipmentInventory || "", // Keep ID
          equipmentInventoryName:
            storeAssetData.equipmentInventoryName || "Unknown Equipment",
          equipmentSubcategory: storeAssetData.equipmentSubCategory || "", // Keep ID
          equipmentSubcategoryName: undefined,
          location: storeAssetData.location || "Unknown Location",
          floor: storeAssetData.floor || "Unknown Floor",
          capacity: storeAssetData.capacity,
          type: storeAssetData.type,
          cylinderNumber: storeAssetData.cylinderNumber,
          panelNo: storeAssetData.panelNo,
          loopNo: storeAssetData.loopNo,
          deviceNo: storeAssetData.deviceNo,
          loopType: storeAssetData.loopType,
          equipmentManufactureDate: storeAssetData.equipmentManufactureDate,
          hptTestDate: storeAssetData.hptTestDate,
          lastRefillDate: storeAssetData.lastRefillDate,
          description:
            (storeAssetData as any).Description ||
            (storeAssetData as any).description,
          qty: storeAssetData.Qty,
          premisesName: storeAssetData.premisesName,
          vendorName: storeAssetData.vendorName,
          mobileNumber: storeAssetData.mobileNumber,
          vendorEmail: storeAssetData.vendorEmail,
          lastInspectionDate: storeAssetData.checksAssets?.[0]?.createdAt,
          inspectionStatus: calculateInspectionStatus(
            storeAssetData.checksAssets
          ),
          nextInspectionDate: storeAssetData?.nextInspectionDate,
          HB_No: storeAssetData?.HB_No || '',
        };
        setSelectedAsset(transformedAsset);
        setCurrentView("asset-details");
      } else {
        setError("Asset not found");
      }
    } catch (err) {
      console.error("Error fetching asset data:", err);
      setError("Failed to load asset data");
    } finally {
      setLoading(false);
    }
  };

  const calculateInspectionStatus = (
    checks: any[]
  ): "due" | "overdue" | "completed" | "upcoming" => {
    if (!checks || checks.length === 0) return "due";

    const lastCheck = checks.sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    )[0];

    if (!lastCheck?.createdAt) return "due";

    const lastCheckDate = new Date(lastCheck.createdAt);
    const now = new Date();
    const daysSinceLastCheck = Math.floor(
      (now.getTime() - lastCheckDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Assuming monthly inspections (30 days)
    if (daysSinceLastCheck > 35) return "overdue";
    if (daysSinceLastCheck > 25) return "due";
    if (daysSinceLastCheck <= 7) return "completed";
    return "upcoming";
  };

  // const handleQRScanSuccess = async (scannedAssetId: string, assetData?: any) => {
  //   // setShowQRScanner(false);

  //   if (assetData) {
  //     // If QR code contains full asset data
  //     setSelectedAsset(assetData);
  //     setCurrentView('asset-details');
  //   } else {
  //     // If QR code only contains asset ID, fetch full data
  //     await fetchAssetData(scannedAssetId);
  //   }
  // };

  // const handleQRScanError = (error: string) => {
  //   console.error('QR Scan Error:', error);
  //   setError('Failed to scan QR code: ' + error);
  // };

  const handleDownloadReport = useCallback(
    async (inspection: { id: string; assetId: string }, format: "pdf" | "excel" = "pdf") => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          "/api/inspection-assessments/download/report",
          {
            params: { id: inspection.id, format },
            responseType: "blob",
          }
        );

        const mimeType = format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: mimeType })
        );
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = `Inspection_Report_${inspection.id}.${format === "excel" ? "xlsx" : "pdf"}`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match) filename = match[1];
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Report downloaded successfully");
      } catch (err) {
        console.error("Error downloading report:", err);
        toast.error("Failed to download report");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleDownloadAllReports = useCallback(
    async (assetId: string) => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          "/api/inspection-assessments/download/report",
          {
            params: { assetId },
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `All_Inspections_${assetId}_${new Date()
            .toISOString()
            .split("T")[0]}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("All reports downloaded successfully");
      } catch (err) {
        console.error("Error downloading reports:", err);
        toast.error("Failed to download reports");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleStartInspection = () => {
    setCurrentView("inspection-form");
  };

  const handleViewHistory = () => {
    setCurrentView("history");
  };

  const handleBackToEquipment = () => {
    // navigate('/safety-equipment');
    window.history.back();
  };

  const renderCurrentView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <DotLoader />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-6 text-center">
          <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setCurrentView("scanner");
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    switch (currentView) {
      case "scanner":
        return (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <QrCode size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Asset Inspection System
            </h2>
            <p className="text-gray-600 mb-6">
              Choose how you want to start an inspection
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* QR Scanner - Primarily for Mobile */}
              <button
                // onClick={() => setShowQRScanner(true)}
                className="px-6 py-3 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors font-medium flex items-center justify-center gap-2"
              >
                <QrCode size={20} />
                Scan QR Code
              </button>

              {/* Direct Asset Selection - For Web */}
              <button
                onClick={() => navigate("/safety-equipment")}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
                Select Asset to Inspect
              </button>
            </div>

            <div className="mt-6 text-sm text-gray-500">
              <p className="mb-2">
                <strong>Mobile:</strong> Use QR scanner for quick field
                inspections
              </p>
              <p>
                <strong>Web:</strong> Select assets directly from the equipment
                list
              </p>
            </div>
          </div>
        );

      case "asset-details":
        return selectedAsset ? (
          <div className="space-y-6">
            <AssetDetails
              asset={selectedAsset as any}
              onStartInspection={handleStartInspection}
            />

            <div className="flex gap-4">
              <button
                onClick={handleViewHistory}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <History size={20} />
                View History
              </button>
            </div>
          </div>
        ) : null;

      case "inspection-form":
        return selectedAsset ? (
          <InspectionForm
            asset={selectedAsset as any}
            onCancel={() => setCurrentView("asset-details")}
          />
        ) : null;

      case "history":
        return selectedAsset ? (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentView("asset-details")}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Asset Details</span>
              </button>
            </div>

            <InspectionHistory
              assetId={selectedAsset.id || ""}
              onViewInspection={(inspection) => {
                setSelectedInspection(inspection);
                setCurrentView("view-inspection");
              }}
              onDownloadReport={(inspection, format) => {
                handleDownloadReport(inspection, format);
              }}
              onDownloadAll={() => {
                handleDownloadAllReports(selectedAsset.id || "");
              }}
            />
          </div>
        ) : null;

      case "view-inspection":
        return selectedInspection ? (
          <InspectionViewer
            inspection={selectedInspection}
            selectedAsset={selectedAsset as AssetData}
            onClose={() => setCurrentView("history")}
            onEdit={() => {
              // TODO: Implement edit functionality
            }}
            onDownloadReport={(format) => {
              handleDownloadReport(selectedInspection, format);
            }}
            canEdit={true}
          />
        ) : null;

      case "checklist-management":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentView("asset-details")}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Asset Details</span>
              </button>
            </div>

            <ChecklistManager
              selectedAssetType={selectedAsset?.equipmentInventory}
              onChecklistSelect={(checklist) => {
                setCurrentView("asset-details");
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-4">
          <div className="flex items-center justify-between px-1 pt-6 mt-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-800">
                Asset Inspection
              </h1>
              {selectedAsset && (
                <p className="text-gray-600 mt-0.5 truncate">
                  {selectedAsset.equipmentInventoryName ||
                    selectedAsset.equipmentInventory}{" "}
                  • {selectedAsset.location}
                </p>
              )}
            </div>

            <button
              onClick={handleBackToEquipment}
              className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
              <span>Back to Safety Equipment</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        {renderCurrentView()}

        {/* QR Scanner Modal */}
        {/* <QRScanner
          isOpen={showQRScanner}
          onClose={() => setShowQRScanner(false)}
          onScanSuccess={handleQRScanSuccess}
          onScanError={handleQRScanError}
        /> */}
      </div>
    </div>
  );
};

export default AssetInspection;
